<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, reactive, computed, ref, onUnmounted } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { AppScreens } from "../../Enums/ChoiceEnums";
    import { ModalStyles, popAlert } from "../../lib/Notification";
    import { IToolbar, ToolbarStyle, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar" 
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { EmrAction, EmrActionData} from '../../lib/Models/QuickPayModel';
    import Toolbar from "../../components/Toolbar.vue"
    import ScreenNoPrivilege from "../../components/ScreenNoPrivilege.vue"
    import * as ChoiceApi from '../../lib/ChoiceApi';

    const privilegesStore = usePrivilegesStore(initPinia());

    const superbillFrame = ref();

    const props = defineProps<{
        patientId: number,
        patientEid: number,
        appointmentId?: number,
        scheduleDate?: string,
        providerEid?: number,
        securityUsersId?: number,
        tabId: number,
    }>();

    enum actionToolbar {
        New,
        Print,
        Save,
        History,
        DisplayHistory,
        DisplayDetails,
        Copy,
        EncounterDate,
        Audit,
        Claim
    }

    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: actionToolbar.New, label: "New", tooltip:"Create a new claim for the patient.", action: ActionButtonStyle.Add, performAction: () => newSuperbill()},
            {identifier: actionToolbar.Print, label: "Print", tooltip:"Print the claim.", action: ActionButtonStyle.Print, performAction: () => printSuperbill()},
            {identifier: actionToolbar.Save, label: "Save", tooltip:"Save changes to the claim.", action: ActionButtonStyle.Save, performAction: () => saveSuperbill()},
            {identifier: actionToolbar.History, label: "", tooltip:"Toggle History/Details.", action: ActionButtonStyle.Menu, menuList: [
                {identifier: actionToolbar.DisplayHistory, label: "Display History"},
                {identifier: actionToolbar.DisplayDetails, label: "Display Details"},
            ]},
            {identifier: actionToolbar.EncounterDate, label: "Encounter", tooltip:"Change the encounter date.", action: ActionButtonStyle.Calendar, performAction: () => changeEncounterDate()},
            {identifier: actionToolbar.Copy, label: "Copy", tooltip:"Copy the selected claim.", action: ActionButtonStyle.Copy, performAction: () => copySuperbill()},
            {identifier: actionToolbar.Audit, label: "Audit", tooltip:"Open the audit history.", action: ActionButtonStyle.Audit, performAction: () => openAudit()},
            {identifier: actionToolbar.Audit, label: "Claim", tooltip:"Open claim window.", action: ActionButtonStyle.Open, performAction: () => openClaim()},
        ]
    });

    const formData = reactive<{
        currentHistory: EmrActionData,
        loadingSuperbill: boolean,
        readSuperbill: boolean,
    }>({
        currentHistory: EmrActionData.DisplaySummary,
        loadingSuperbill: true,
        readSuperbill: true
    });

    async function getPrivileges() {
        await privilegesStore.getScreens([AppScreens.CheckInOutDashboard, AppScreens.EMRSuperbill, AppScreens.ChargeEntry]);
    }

    async function fetchData() {
        const promisePrivileges = getPrivileges();
        await Promise.all([promisePrivileges]);
        formData.readSuperbill = privilegesStore.screensWithReadPrivileges[AppScreens.EMRSuperbill]
    }
    fetchData();

    onMounted(async () => {
        MessageLib.Emr.sink.pageAction(superbillActionSink);
        switchAction(formData.currentHistory);
    });

    onUnmounted(async () => {
        MessageLib.Emr.sink.pageActionDeleteSink(superbillActionSink);
    });

    const superbillUrl = computed<string>(() => `/officemd/screens/EMRSuperbill.asp?Launch=ReceiptEntryForm&NO_RESIZE=1`);

    function performSuperbillAction(actionType: string, actionValue: string) {
        superbillFrame.value.contentWindow["fMain"].emr_request_portal_action(actionType, actionValue);
    }

    function emrSuperbillLoaded() {
        superbillFrame.value.contentWindow["fMain"].emr_request_clear_data();

        const BuildEMRInputs = ChoiceApi.quickPay.BuildEMRInputs({
            securityUsersId: props.securityUsersId, 
            patientId: props.patientId, 
            patientEid: props.patientEid, 
            providerEid: props.providerEid,
            appointmentId: props.appointmentId,
            walkInPatient: "N",
            encounterDate: props.scheduleDate?.substring(0,10),
            patientPullType: "S"
        });
        superbillFrame.value.contentWindow["fMain"].emr_request_get_tab_data(BuildEMRInputs);
        performSuperbillAction(EmrAction.History, EmrActionData.DisplaySummary);
        formData.loadingSuperbill = false;
    }

    async function newSuperbill() {
        if (!privilegesStore.screensWithWritePrivileges[AppScreens.EMRSuperbill]) {
            popAlert("You do not have access to perform this function. Please contact your System Administrator.", "New Superbill", ModalStyles.info);
            return;
        }
        performSuperbillAction(EmrAction.New, EmrActionData.NoData);
    }
    async function printSuperbill() {
        performSuperbillAction(EmrAction.Print, EmrActionData.NoData);
    }
    async function saveSuperbill() {
        if (!privilegesStore.screensWithUpdatePrivileges[AppScreens.EMRSuperbill]) {
            popAlert("You do not have access to perform this function. Please contact your System Administrator.", "Save Superbill", ModalStyles.info);
            return;
        }
        performSuperbillAction(EmrAction.Save, EmrActionData.NoData);
    }
    async function copySuperbill() {
        if (!privilegesStore.screensWithWritePrivileges[AppScreens.EMRSuperbill]) {
            popAlert("You do not have access to perform this function. Please contact your System Administrator.", "Copy Superbill", ModalStyles.info);
            return;
        }
        if (formData.currentHistory == EmrActionData.DisplayHistory)
            performSuperbillAction(EmrAction.Copy, EmrActionData.NoData);
    }
    async function changeEncounterDate() {
        performSuperbillAction(EmrAction.EncounterDate, EmrActionData.NoData);
    }

    async function openAudit() {
        performSuperbillAction(EmrAction.Audit, EmrActionData.NoData);
    }

    async function openClaim() {
        if (!privilegesStore.screensWithReadPrivileges[AppScreens.ChargeEntry]) {
            popAlert("You do not have access to perform this function. Please contact your System Administrator.", "Open Claim", ModalStyles.info);
            return;
        }
        performSuperbillAction(EmrAction.OpenClaim, EmrActionData.NoData);
    }

    async function toolbarClick(data: actionToolbar): Promise<void> {
        if (data == actionToolbar.History) {
            formData.currentHistory = formData.currentHistory == EmrActionData.DisplayHistory ? EmrActionData.DisplaySummary : EmrActionData.DisplayHistory;
            performSuperbillAction(EmrAction.History, formData.currentHistory);
            switchAction(formData.currentHistory);
        }
        if (data === actionToolbar.DisplayHistory) {
            performSuperbillAction(EmrAction.History, EmrActionData.DisplayHistory);
            switchAction(EmrActionData.DisplayHistory);
        }
        else if (data == actionToolbar.DisplayDetails) {
            performSuperbillAction(EmrAction.History, EmrActionData.DisplaySummary);
            switchAction(EmrActionData.DisplaySummary);
        }
    }

    function switchAction(action: string) {
        const index = toolbarConfig.toolbarList.findIndex(item => item.identifier == actionToolbar.Copy)
        if (index >= 0) {
            toolbarConfig.toolbarList[index].enabled = (action == EmrActionData.DisplaySummary ? false : true);
        }
    }

	async function superbillActionSink(payload: MessageLib.Emr.EmrPageActionPayload) {
        if (payload.location == AppScreens.EMRSuperbill) {
            if (payload.action == "emr_response_get_tab_html" && payload.data == false) {
                popAlert("There was a problem loading the claim tab.  Please close the Check In/Out dashboard and try again.", "Loading Claim", ModalStyles.danger);
            }
            else if (payload.action == EmrAction.History) {
                if (payload.data == EmrActionData.DisplayHistory) {
                    formData.currentHistory = EmrActionData.DisplaySummary;
                    switchAction(formData.currentHistory);
                }
            }
        }
    }

</script>

<style scoped>
    .superbill {
        height: 600px;
    }
</style>

<template>
    <div class="row mt-2" v-if="formData.readSuperbill">
        <div class="col">
            <Toolbar 
                :configuration="toolbarConfig" 
                :toolbarStyle="ToolbarStyle.outline" 
                :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                :toolbarBackground="ToolbarBackground.white"
                @toolbarClick="toolbarClick"/>  
        </div>
        <div class="w-100"></div>
        <div class="superbill mt-2">
            <div v-if="formData.loadingSuperbill">Loading Claim...</div>
            <iframe 
                ref="superbillFrame" 
                :src="superbillUrl" 
                class="border-isalus-primary"
                title="Claim" 
                :style="{'overflow-y': 'hidden', 'overflow-x': 'hidden', 'width': '100%', 'height': '100%'}" 
                @load="emrSuperbillLoaded()"></iframe>
        </div>
    </div>
    <div v-else>
        <ScreenNoPrivilege :screen="AppScreens.EMRSuperbill" />
    </div>
</template>

